/**
 * iter84.56.8 — Tech-debt split from ReleaseOpsAuditCards.jsx.
 * Mechanical extraction; no behavior change.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    RefreshCw, AlertTriangle, CheckCircle2, 
    Loader2, 
    Sparkles,
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";

export function ScoringDriftHealCard({ environment = "preview" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [healing, setHealing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [healResult, setHealResult] = useState(null);

    const fetchOnce = useCallback(async (showSpinner) => {
        if (showSpinner) setRefreshing(true);
        try {
            const r = await api.releaseOpsScoringDriftPreview();
            setData(r);
            setError(null);
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to scan scoring drift");
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOnce(false); }, [fetchOnce]);

    const affected = data?.tournaments_affected ?? 0;
    const verdict = error
        ? "fail"
        : affected === 0
            ? "clear"
            : "warn";

    const badge = data
        ? affected === 0
            ? "No drift"
            : `${affected} need healing`
        : (environment === "production" ? "production" : "preview");
    const badgeClass = affected === 0
        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
        : "bg-amber-500/15 text-amber-300 border-amber-500/40";

    const refreshBtn = (
        <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); fetchOnce(true); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); fetchOnce(true); } }}
            data-testid="scoring-drift-refresh-btn"
            className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
        >
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Re-scan
        </span>
    );

    const runHeal = async () => {
        setHealing(true);
        try {
            const r = await api.releaseOpsZeroEntryStageReachPoints();
            setHealResult(r);
            if (r.tournaments_patched === 0) {
                toast.success("Already healed — no tournaments needed patching.");
            } else {
                toast.success(
                    `Healed ${r.tournaments_patched} tournament${r.tournaments_patched === 1 ? "" : "s"}. ` +
                    `Entry-stage reach-points zeroed.`,
                );
            }
            setConfirmOpen(false);
            await fetchOnce(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Heal failed");
        } finally {
            setHealing(false);
        }
    };

    return (
        <CollapsibleCard
            testId="scoring-drift-heal-card"
            icon={Sparkles}
            title="Heal scoring drift (entry-stage reach points)"
            badge={badge}
            badgeClass={badgeClass}
            verdict={verdict}
            headerExtra={refreshBtn}
        >
            <div
                data-environment={environment}
                data-tournaments-affected={affected}
            >
                {loading && (
                    <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Scanning tournaments for entry-stage drift…
                    </p>
                )}
                {error && (
                    <p className="text-xs text-rose-400" data-testid="scoring-drift-error">
                        {error}
                    </p>
                )}
                {!loading && !error && data && (
                    <>
                        <p className="text-[11px] text-zinc-400 leading-relaxed mb-3">
                            Zeros <code className="text-[9px] bg-zinc-800 px-1 rounded">reach_&#123;firststage&#125;_points</code> on every tournament whose <em>first</em> stage is a knockout round (r64, r32, r16, qf, sf, or final). In those tournaments every drafted team automatically starts at the first stage, so the "reach" reward awards a flat amount to everyone and doesn't change standings — it just inflates totals. <span className="text-zinc-500">Applies to ALL templates (WC KO, March Madness, custom KO, etc.) — keys on <code className="text-[9px] bg-zinc-800 px-1 rounded">stages[0]</code>, not template name.</span> Idempotent, safe to re-run.
                        </p>

                        {healResult && (
                            <p
                                data-testid="scoring-drift-last-result"
                                className={`mb-3 text-[10px] font-mono ${healResult.tournaments_patched > 0 ? "text-emerald-300" : "text-zinc-500"}`}
                            >
                                Last run: {healResult.tournaments_patched} tournament{healResult.tournaments_patched === 1 ? "" : "s"} patched @ {new Date(healResult.ran_at).toLocaleString()}
                            </p>
                        )}

                        {affected === 0 ? (
                            <p
                                data-testid="scoring-drift-empty"
                                className="text-[11px] text-emerald-300 italic flex items-center gap-1.5"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Clean — every knockout-first tournament has its entry-stage reach-points already zeroed.
                            </p>
                        ) : (
                            <>
                                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                                    <p className="text-[11px] text-amber-300 font-bold flex items-center gap-1.5">
                                        <AlertTriangle className="w-3.5 h-3.5" />
                                        {affected} tournament{affected === 1 ? "" : "s"} need healing
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setConfirmOpen(true)}
                                        disabled={healing}
                                        data-testid="scoring-drift-heal-btn"
                                        className="brutal-btn-volt text-[10px] py-1 px-3 inline-flex items-center gap-1 shrink-0 disabled:opacity-50"
                                    >
                                        {healing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                        {healing ? "Healing…" : "Heal now"}
                                    </button>
                                </div>
                                <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="scoring-drift-rows">
                                    {data.rows.map((row) => (
                                        <li
                                            key={`${row.tournament_id}-${row.field}`}
                                            data-testid={`scoring-drift-row-${row.tournament_id}`}
                                            className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-2"
                                        >
                                            <div className="flex items-center gap-2 flex-wrap text-[11px]">
                                                <span className="font-bold text-white">{row.tournament_name}</span>
                                                <code className="text-[9px] text-zinc-500 truncate">{row.tournament_id}</code>
                                                {row.template && (
                                                    <span className="text-[9px] text-zinc-500">· {row.template}</span>
                                                )}
                                            </div>
                                            <div className="mt-1 flex items-center gap-2 text-[10px] flex-wrap">
                                                <span className="text-zinc-400">Entry stage:</span>
                                                <code className="text-[10px] bg-zinc-800 px-1 rounded text-cyber">{row.entry_stage}</code>
                                                <span className="text-zinc-400">·</span>
                                                <span className="text-zinc-400">{row.field}:</span>
                                                <span className="font-mono text-amber-300 font-bold">{row.current_value}</span>
                                                <span className="text-zinc-500">→</span>
                                                <span className="font-mono text-emerald-300 font-bold">0</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <p className="text-[10px] text-zinc-500 italic mt-2 font-mono">
                            scanned {new Date(data.scanned_at).toLocaleString()}
                        </p>
                    </>
                )}
            </div>

            {confirmOpen && (
                <div
                    data-testid="scoring-drift-confirm-modal"
                    onClick={() => !healing && setConfirmOpen(false)}
                    className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-zinc-950 border-2 border-volt/60 shadow-2xl rounded-sm max-w-lg w-full p-4"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-5 h-5 text-volt" />
                            <h3 className="text-sm font-bold tracking-widest uppercase text-white">Confirm heal</h3>
                        </div>
                        <p className="text-[11px] text-zinc-300 mb-3 leading-relaxed">
                            About to zero <code className="text-[10px] bg-zinc-800 px-1 rounded">reach_&#123;firststage&#125;_points</code> on{" "}
                            <span className="font-bold text-white">{affected} tournament{affected === 1 ? "" : "s"}</span>.
                            This writes to the database. The operation is idempotent — re-running on a clean DB does nothing — but the change is not auto-rollback-able.
                        </p>
                        <div className="text-[10px] text-zinc-500 font-mono mb-3 max-h-32 overflow-y-auto border border-zinc-800 rounded-sm p-2 bg-zinc-900/40">
                            {data?.rows.map((row) => (
                                <div key={`confirm-${row.tournament_id}-${row.field}`}>
                                    {row.tournament_name} · {row.field}: {row.current_value} → 0
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setConfirmOpen(false)}
                                disabled={healing}
                                data-testid="scoring-drift-confirm-cancel"
                                className="brutal-btn-ghost text-[10px] py-1 px-3 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={runHeal}
                                disabled={healing}
                                data-testid="scoring-drift-confirm-heal"
                                className="brutal-btn-volt text-[10px] py-1 px-3 inline-flex items-center gap-1 disabled:opacity-50"
                            >
                                {healing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                {healing ? "Healing…" : "Yes — heal all"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </CollapsibleCard>
    );
}


// ---------------------------------------------------------------------------
// iter84.56.6.5 — AuditAcknowledgementLogCard
//
// Lists all active (non-revoked) audit acknowledgements with their reason,
// who acked them and when. Operator can revoke any ack from here so they
// don't have to wait for the state signature to flip back into FAIL.
// Toggle "include revoked" to see the full historical log.
// ---------------------------------------------------------------------------

